import { AddOrEditFileReferenceSlideover } from "@/components/features/matter/file-repo/AddOrEditFileReferenceSlideover";
import { LoadingSpinner, Typography } from "@/components/ui";
import { Button } from "@/components/ui/shadcn/Button/Button";

import { useCreateMatterFileReference } from "@/lib/data/react-query/matters/matter-file";
import type { MatterFileReferenceSchema } from "@/lib/data/schemas";
import type { Matter, MatterFile } from "@/lib/types/Matter";
import { PlusIcon, Upload } from "lucide-react";
import { useState } from "react";

export const UploadFileReferenceFromStorageButton = ({
  matter,
  itemKey,
  itemId,
  disabled = false,
  disabledMessage = "You do not have permission to upload file references",
}: {
  matter: Matter;
  itemKey: keyof typeof MatterFileReferenceSchema.shape;
  itemId: string;
  disabled?: boolean;
  disabledMessage?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [autoOpenNewFileUpload, setAutoOpenNewFileUpload] = useState(false);
  const createFileReference = useCreateMatterFileReference();

  const handleCreateMatterFileReference = async (
    selectedFiles: MatterFile[],
  ) => {
    await Promise.all(
      selectedFiles.map((file) =>
        createFileReference.mutateAsync({
          matterId: matter.id,
          fileId: file.id,
          [itemKey]: itemId,
        }),
      ),
    );
    setOpen(false);
  };
  return (
    <div className="flex w-full flex-col gap-2">
      <div>
        <Button
          variant="outline"
          className="h-40 w-full flex-col bg-transparent px-2"
          size="sm"
          title="Upload options"
          type="button"
          disabled={disabled}
          onClick={() => {
            setAutoOpenNewFileUpload(false);
            setOpen(true);
          }}
        >
          <div className="flex flex-col justify-center gap-2">
            <div className="flex items-center justify-center gap-2">
              <PlusIcon className="h-4 w-4" />
              {createFileReference.isPending ? (
                <LoadingSpinner />
              ) : (
                <Typography>New file reference</Typography>
              )}
            </div>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setAutoOpenNewFileUpload(true);
                setOpen(true);
              }}
              type="button"
              variant="ghost"
              className="h-[25px] w-full cursor-pointer hover:underline"
              size="icon"
            >
              <Typography className="flex w-full gap-x-2" variant="subHeading">
                <Upload className="h-4 w-4" /> or upload file
              </Typography>
            </Button>
          </div>
          <span className="sr-only">Upload file reference</span>
        </Button>
      </div>
      {disabled && disabledMessage ? (
        <Typography tag="p" size="xs" className="text-muted-foreground">
          {disabledMessage}
        </Typography>
      ) : null}
      <AddOrEditFileReferenceSlideover
        open={open}
        setOpen={setOpen}
        matter={matter}
        handleCreateMatterFileReference={handleCreateMatterFileReference}
        isCreating={createFileReference.isPending}
        autoOpenNewFileUpload={autoOpenNewFileUpload}
      />
    </div>
  );
};
